import { Grid, Stack, Typography } from "@mui/material";
import {
  TextInputField,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import React from "react";
import { useTranslation } from "react-i18next";
import { getEmailCycleBasicInformationValidationErrorMessage } from "../../utils/getEmailCycleBasicInformationValidationErrorMessage";
import { Instance } from "mobx-state-tree";
import { BoundedNameModel } from "../../../models/BoundedNameModel";
import { KeycloakEmailModel } from "../../../models/KeycloakEmailModel";
import { observer } from "mobx-react";
import { ReadOnlyFieldContainer } from "../inputs/ReadOnlyFieldContainer";

const FORM_MAX_WIDTH = "600px";

interface EmailCycleBasicInformationFormProps {
  cycleName: Instance<typeof BoundedNameModel>;
  displayCycleName?: string;
  fromEmailId: Instance<typeof KeycloakEmailModel>;
  displayName: Instance<typeof BoundedNameModel>;
  subject: Instance<typeof BoundedNameModel>;
  fromEmailIdApplicationErrorMessage?: string;
  onFromEmailIdChange?: (value: string) => void;
  readOnly?: boolean;
}

export const EmailCycleBasicInformationForm: React.FC<EmailCycleBasicInformationFormProps> =
  observer(
    ({
      cycleName,
      displayCycleName,
      fromEmailId,
      displayName,
      subject,
      fromEmailIdApplicationErrorMessage,
      onFromEmailIdChange,
      readOnly = false,
    }) => {
      const { t } = useTranslation();
      const spacing = useSpacing();
      const typography = useTypography();

      const validationErrorMessage =
        getEmailCycleBasicInformationValidationErrorMessage(t, {
          cycleName: cycleName.validationError,
          fromEmailId: fromEmailId.validationError,
          displayName: displayName.validationError,
          subject: subject.validationError,
        });
      const fromEmailIdHelperText =
        validationErrorMessage.fromEmailId ??
        fromEmailIdApplicationErrorMessage;

      return (
        <Stack gap={spacing.spaceXL}>
          {/* Title */}
          <Typography {...typography.sh2}>
            {t("emailCycles.basicInformation")}
          </Typography>

          {/* Form */}
          <Grid
            container
            maxWidth={FORM_MAX_WIDTH}
            spacing={spacing.spaceLG}
            columns={2}
          >
            {/* Cycle Name field */}
            <Grid size={2}>
              {readOnly ? (
                <ReadOnlyFieldContainer label={t("emailCycles.cycleNameLabel")}>
                  {displayCycleName ?? cycleName.name}
                </ReadOnlyFieldContainer>
              ) : (
                <TextInputField
                  name="cycle"
                  value={cycleName.name}
                  type="text"
                  onTextChange={cycleName.setName}
                  placeholder={t("emailCycles.emailCycleNamePlaceholder")}
                  label={t("emailCycles.cycleNameLabel")}
                  error={!!validationErrorMessage.cycleName}
                  helperText={validationErrorMessage.cycleName}
                  required
                />
              )}
            </Grid>

            {/* From email ID field */}
            <Grid size={1}>
              {readOnly ? (
                <ReadOnlyFieldContainer
                  label={t("emailCycles.fromEmailIdLabel")}
                >
                  {fromEmailId.value}
                </ReadOnlyFieldContainer>
              ) : (
                <TextInputField
                  name="fromEmailId"
                  value={fromEmailId.value}
                  type="text"
                  onTextChange={onFromEmailIdChange ?? fromEmailId.setValue}
                  placeholder={t("emailCycles.fromEmailIdPlaceholder")}
                  label={t("emailCycles.fromEmailIdLabel")}
                  error={!!fromEmailIdHelperText}
                  helperText={fromEmailIdHelperText}
                  required
                />
              )}
            </Grid>

            {/* Display name field */}
            <Grid size={1}>
              {readOnly ? (
                <ReadOnlyFieldContainer
                  label={t("emailCycles.displayNameLabel")}
                >
                  {displayName.name}
                </ReadOnlyFieldContainer>
              ) : (
                <TextInputField
                  name="displayName"
                  value={displayName.name}
                  type="text"
                  onTextChange={displayName.setName}
                  placeholder={t("emailCycles.displayNamePlaceholder")}
                  label={t("emailCycles.displayNameLabel")}
                  error={!!validationErrorMessage.displayName}
                  helperText={validationErrorMessage.displayName}
                  required
                />
              )}
            </Grid>

            {/* Subject field */}
            <Grid size={2}>
              {readOnly ? (
                <ReadOnlyFieldContainer label={t("emailCycles.subject")}>
                  {subject.name}
                </ReadOnlyFieldContainer>
              ) : (
                <TextInputField
                  name="subject"
                  value={subject.name}
                  type="text"
                  onTextChange={subject.setName}
                  placeholder={t("emailCycles.subjectPlaceholder")}
                  label={t("emailCycles.subject")}
                  error={!!validationErrorMessage.subject}
                  helperText={validationErrorMessage.subject}
                  required
                />
              )}
            </Grid>
          </Grid>
        </Stack>
      );
    },
  );
